import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import fr from './locales/fr.json';

const I18nContext = createContext(null);

const dictionaries = { en, fr };

function getByPath(dictionary, path) {
	if (!path) return '';
	const parts = path.split('.');
	let current = dictionary;
	for (const part of parts) {
		if (current && Object.prototype.hasOwnProperty.call(current, part)) {
			current = current[part];
		} else {
			return path; // fallback to key
		}
	}
	return current;
}

function interpolate(template, vars) {
	if (!template || !vars) return template;
	return template.replace(/\{(\w+)\}/g, (match, key) => {
		return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
	});
}

export const I18nProvider = ({ children }) => {
	const [locale, setLocale] = useState(() => localStorage.getItem('locale') || 'en');

	useEffect(() => {
		localStorage.setItem('locale', locale);
		document.documentElement.lang = locale;
	}, [locale]);

	const t = useCallback((key, vars) => {
		const dict = dictionaries[locale] || dictionaries.en;
		const text = getByPath(dict, key);
		if (typeof text !== 'string') return text;
		return interpolate(text, vars);
	}, [locale]);

	const value = useMemo(() => ({ locale, setLocale, t }), [locale, t]);

	return (
		<I18nContext.Provider value={value}>
			{children}
		</I18nContext.Provider>
	);
};

export const useI18n = () => {
	const ctx = useContext(I18nContext);
	if (!ctx) throw new Error('useI18n must be used within I18nProvider');
	return ctx;
};

